import { useState, useCallback } from 'react';
import { ScheduleItem } from '@app-types';

interface UseSchedulePageModalsReturn {
  // Modal states
  showScheduledDateModal: boolean;
  showAssessorModal: boolean;
  showManagerModal: boolean;
  showOwnerModal: boolean;
  showAddToGroupModal: boolean;
  showRemoveConfirm: boolean;

  // Selected items
  selectedItem: ScheduleItem | null;
  selectedContractId: number | null;
  selectedIsGroupOperation: boolean;
  groupToAddTo: ScheduleItem | null;
  removeGroupId: number | null;
  removeContractId: number | null;

  // Modal handlers
  openScheduledDateModal: (item: ScheduleItem) => void;
  closeScheduledDateModal: () => void;

  openAssessorModal: (item: ScheduleItem) => void;
  closeAssessorModal: () => void;

  openManagerModal: (item: ScheduleItem, contractId: number) => void;
  closeManagerModal: () => void;

  openOwnerModal: (item: ScheduleItem, contractId: number, isGroupOperation?: boolean) => void;
  closeOwnerModal: () => void;

  openAddToGroupModal: (item: ScheduleItem) => void;
  closeAddToGroupModal: () => void;

  openRemoveConfirm: (groupId: number, contractId: number) => void;
  closeRemoveConfirm: () => void;
}

/**
 * Generic hook for managing modals in schedule pages (Scheduler & Assessor)
 * Handles: modal visibility, selected items, open/close handlers
 */
export function useSchedulePageModals(): UseSchedulePageModalsReturn {
  // Modal visibility states
  const [showScheduledDateModal, setShowScheduledDateModal] = useState(false);
  const [showAssessorModal, setShowAssessorModal] = useState(false);
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Selected items states
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [selectedIsGroupOperation, setSelectedIsGroupOperation] = useState<boolean>(false);
  const [groupToAddTo, setGroupToAddTo] = useState<ScheduleItem | null>(null);
  const [removeGroupId, setRemoveGroupId] = useState<number | null>(null);
  const [removeContractId, setRemoveContractId] = useState<number | null>(null);

  // Scheduled Date Modal
  const openScheduledDateModal = useCallback((item: ScheduleItem) => {
    setSelectedItem(item);
    setShowScheduledDateModal(true);
  }, []);

  const closeScheduledDateModal = useCallback(() => {
    setShowScheduledDateModal(false);
    setSelectedItem(null);
  }, []);

  // Assessor Modal
  const openAssessorModal = useCallback((item: ScheduleItem) => {
    setSelectedItem(item);
    setShowAssessorModal(true);
  }, []);

  const closeAssessorModal = useCallback(() => {
    setShowAssessorModal(false);
    setSelectedItem(null);
  }, []);

  // Manager Modal
  const openManagerModal = useCallback((item: ScheduleItem, contractId: number) => {
    setSelectedItem(item);
    setSelectedContractId(contractId);
    setShowManagerModal(true);
  }, []);

  const closeManagerModal = useCallback(() => {
    setShowManagerModal(false);
    setSelectedItem(null);
    setSelectedContractId(null);
  }, []);

  // Owner Modal
  const openOwnerModal = useCallback((item: ScheduleItem, contractId: number, isGroupOperation?: boolean) => {
    setSelectedItem(item);
    setSelectedContractId(contractId);
    setSelectedIsGroupOperation(isGroupOperation || false);
    setShowOwnerModal(true);
  }, []);

  const closeOwnerModal = useCallback(() => {
    setShowOwnerModal(false);
    setSelectedItem(null);
    setSelectedContractId(null);
    setSelectedIsGroupOperation(false);
  }, []);

  // Add to Group Modal
  const openAddToGroupModal = useCallback((item: ScheduleItem) => {
    setGroupToAddTo(item);
    setShowAddToGroupModal(true);
  }, []);

  const closeAddToGroupModal = useCallback(() => {
    setShowAddToGroupModal(false);
    setGroupToAddTo(null);
  }, []);

  // Remove from Group Confirm
  const openRemoveConfirm = useCallback((groupId: number, contractId: number) => {
    setRemoveGroupId(groupId);
    setRemoveContractId(contractId);
    setShowRemoveConfirm(true);
  }, []);

  const closeRemoveConfirm = useCallback(() => {
    setShowRemoveConfirm(false);
    setRemoveGroupId(null);
    setRemoveContractId(null);
  }, []);

  return {
    // Modal states
    showScheduledDateModal,
    showAssessorModal,
    showManagerModal,
    showOwnerModal,
    showAddToGroupModal,
    showRemoveConfirm,

    // Selected items
    selectedItem,
    selectedContractId,
    selectedIsGroupOperation,
    groupToAddTo,
    removeGroupId,
    removeContractId,

    // Handlers
    openScheduledDateModal,
    closeScheduledDateModal,
    openAssessorModal,
    closeAssessorModal,
    openManagerModal,
    closeManagerModal,
    openOwnerModal,
    closeOwnerModal,
    openAddToGroupModal,
    closeAddToGroupModal,
    openRemoveConfirm,
    closeRemoveConfirm
  };
}
